import { BasicValues } from "@interfaces/mgr/SEIKATSUKAIGO/facility/basic";
import { SubtractionItemValues } from "@interfaces/mgr/SEIKATSUKAIGO/facility/subtractionItem";
import { AdditionalItemValues } from "@interfaces/mgr/SEIKATSUKAIGO/facility/additionItem";
import { FacilityState } from "@stores/domain/mgr/SEIKATSUKAIGO/facility/types";
import { dateToSelectDateValueYYYYM } from "@utils/date";
import { AdministrationValues } from "@interfaces/mgr/SEIKATSUKAIGO/facility/administration";
import {
  DEFAULT_SELECT_VALUE,
  NO_SELECTED_CONDITION_TYPE_2024,
  NO_SELECTED_CONDITION_TYPE_2024_SUB,
  SEE_HEAR_TEAM_202404_DEFAULT
} from "@constants/variables";
import initializeSelectDate from "@utils/domain/facility/initializeSelectDate";
import { initializeSpecificBetterSupporterCondition202404V } from "@utils/domain/facility/initializeSpecificBetterSupporterCondition202404V";

export type FacilityValues = BasicValues &
  AdministrationValues &
  SubtractionItemValues &
  AdditionalItemValues;

const initialValues = (state?: FacilityState): FacilityValues => {
  return {
    basic: {
      corporationName: state ? state.corporationName : "",
      officeNumber: state ? state.officeNumber : "",
      officeName: state ? state.officeName : "",
      serviceType: state ? state.serviceType : "",
      facilityType: state ? `${state.facilityType}` : "",
      representativeName: state ? state.representativeName : "",
      capacity: state ? state.capacity : "",
      masterSubordinateFlg: state ? state.masterSubordinateFlg : false,
      masterFlg: state ? state.masterFlg : "1",
      multiFunctionOfficeFlag: state ? state.multiFunctionOfficeFlag : false,
      allCapacity: state ? state.allCapacity : "",
      postalCode: state ? state.postalCode : "",
      prefectureId: state ? state.selectedPrefectureName : "",
      cityId: state ? state.selectedCityCode : "",
      restAddress: state ? state.restAddress : "",
      tel: state ? state.tel : "",
      mealSaservedServiceFlag: state ? state.mealSaservedServiceFlag : false,
      transferServiceFlag: state ? state.transferServiceFlag : false,
      transferServiceType: state ? state.transferServiceType : "1",
      seriousSupporterFlg: state ? state.seriousSupporterFlg : false,
      commuterLifeSupportFlag: state ? state.commuterLifeSupportFlag : false
    },
    administration: {
      mondaySchedule: state ? state.mondaySchedule : false,
      mondayStartTime: state ? state.mondayStartTime : "",
      mondayEndTime: state ? state.mondayEndTime : "",
      tuesdaySchedule: state ? state.tuesdaySchedule : false,
      tuesdayStartTime: state ? state.tuesdayStartTime : "",
      tuesdayEndTime: state ? state.tuesdayEndTime : "",
      wednesdaySchedule: state ? state.wednesdaySchedule : false,
      wednesdayStartTime: state ? state.wednesdayStartTime : "",
      wednesdayEndTime: state ? state.wednesdayEndTime : "",
      thursdaySchedule: state ? state.thursdaySchedule : false,
      thursdayStartTime: state ? state.thursdayStartTime : "",
      thursdayEndTime: state ? state.thursdayEndTime : "",
      fridaySchedule: state ? state.fridaySchedule : false,
      fridayStartTime: state ? state.fridayStartTime : "",
      fridayEndTime: state ? state.fridayEndTime : "",
      saturdaySchedule: state ? state.saturdaySchedule : false,
      saturdayStartTime: state ? state.saturdayStartTime : "",
      saturdayEndTime: state ? state.saturdayEndTime : "",
      sundaySchedule: state ? state.sundaySchedule : false,
      sundayStartTime: state ? state.sundayStartTime : "",
      sundayEndTime: state ? state.sundayEndTime : ""
    },
    subtractionItem: {
      establishedByLocalGovernmentsFlag: state
        ? state.establishedByLocalGovernmentsFlag
        : false,
      openedOrShortTimeFlag: state ? state.openedOrShortTimeFlag : false,
      lackFlag: state
        ? state.lackOfLifeSupportMemberFlag || state.lackOfResponsiblePersonFlag
        : false,
      lackOfLifeSupportMemberFlag: state
        ? state.lackOfLifeSupportMemberFlag
        : false,
      lackOfLifeSupportMemberStartDate:
        state && state.lackOfLifeSupportMemberStartDate
          ? dateToSelectDateValueYYYYM(state.lackOfLifeSupportMemberStartDate)
          : { year: DEFAULT_SELECT_VALUE, month: "", day: "1" }, // APIの仕様上dayは1固定
      lackOfResponsiblePersonFlag: state
        ? state.lackOfResponsiblePersonFlag
        : false,
      lackOfResponsiblePersonStartDate:
        state && state.lackOfResponsiblePersonStartDate
          ? dateToSelectDateValueYYYYM(state.lackOfResponsiblePersonStartDate)
          : { year: DEFAULT_SELECT_VALUE, month: "", day: "1" }, // APIの仕様上dayは1固定
      doctorInstallationFlag: state ? state.doctorInstallationFlag : false,
      bodyRestrictedStillFlg: state ? state.bodyRestrictedStillFlg : false,
      bodyRestrictedStillFrom: initializeSelectDate(
        state && state.bodyRestrictedStillFrom
      ),
      bodyRestrictedStillTo: initializeSelectDate(
        state && state.bodyRestrictedStillTo
      ),
      abusePreventionMeasuresNotImplementedFlg: state
        ? state.abusePreventionMeasuresNotImplementedFlg
        : false,
      abusePreventionMeasuresNotImplementedFrom: initializeSelectDate(
        state && state.abusePreventionMeasuresNotImplementedFrom
      ),
      abusePreventionMeasuresNotImplementedTo: initializeSelectDate(
        state && state.abusePreventionMeasuresNotImplementedTo
      ),
      workContinuationNotPlanningFlg: state
        ? state.workContinuationNotPlanningFlg
        : false,
      workContinuationNotPlanningFrom: initializeSelectDate(
        state && state.workContinuationNotPlanningFrom
      ),
      workContinuationNotPlanningTo: initializeSelectDate(
        state && state.workContinuationNotPlanningTo
      ),
      informationDisclosureNotReportedFlg: state
        ? state.informationDisclosureNotReportedFlg
        : false,
      informationDisclosureNotReportedFrom: initializeSelectDate(
        state && state.informationDisclosureNotReportedFrom
      ),
      informationDisclosureNotReportedTo: initializeSelectDate(
        state && state.informationDisclosureNotReportedTo
      )
    },
    additionalItem: {
      staffPlacementType: state ? state.staffPlacementType : "0",
      welfareSpecialistPlacementType: state
        ? state.welfareSpecialistPlacementType
        : "1",
      welfareConditionStartDate: initializeSelectDate(
        state && state.welfareConditionStartDate
      ),
      welfareConditionEndDate: initializeSelectDate(
        state && state.welfareConditionEndDate
      ),
      nursingSupporterFlg: state ? state.nursingSupporterFlg : false,
      nursingSupporter: state ? state.nursingSupporter : "",
      specificBetterSupporterCondition202404: state
        ? initializeSpecificBetterSupporterCondition202404V(
            state.specificBetterSupporterCondition202404
          )
        : NO_SELECTED_CONDITION_TYPE_2024,
      specificBetterSupporterCondition202404Sub:
        state && state.specificBetterSupporterCondition202404
          ? state.specificBetterSupporterCondition202404
          : NO_SELECTED_CONDITION_TYPE_2024_SUB,
      staffTreatmentSystemType: state ? state.staffTreatmentSystemType : "1",
      staffTreatmentSpecificSystemType: state
        ? state.staffTreatmentSpecificSystemType
        : "1",
      betterSupporterConditionBaseUpFlg: state
        ? state.betterSupporterConditionBaseUpFlg
        : false,
      seriousDisability: state ? state.seriousDisability : "0",
      visualAuditoryLanguageDisabledPeopleSupportSystemFlag: state
        ? state.visualAuditoryLanguageDisabledPeopleSupportSystemFlag
        : false,
      employmentTransitionSupportFlag: state
        ? state.employmentTransitionSupportFlag
        : false,
      numberOfContinuations: state ? state.numberOfContinuations : "",
      placementOfServiceAdminFlag: state
        ? state.placementOfServiceAdminFlag
        : false,
      placementStartDate: initializeSelectDate(
        state && state.placementStartDate
      ),
      placementEndDate: initializeSelectDate(state && state.placementEndDate),
      welfareConditionCombined: state ? state.welfareConditionCombined : "",
      welfareSpecialistPlacementType1: state
        ? state.welfareSpecialistPlacementType1
        : false,
      welfareSpecialistPlacementType2: state
        ? state.welfareSpecialistPlacementType2
        : false,
      welfareSpecialistPlacementType3: state
        ? state.welfareSpecialistPlacementType3
        : false,
      seeHearTeam202404: state
        ? state.seeHearTeam202404
        : SEE_HEAR_TEAM_202404_DEFAULT,
      seeHearTeam202404From: initializeSelectDate(
        state && state.seeHearTeam202404From
      ),
      seeHearTeam202404To: initializeSelectDate(
        state && state.seeHearTeam202404To
      ),
      higherBrainDysfunctionPersonsSupportFlg: state
        ? state.higherBrainDysfunctionPersonsSupportFlg
        : false,
      higherBrainDysfunctionPersonsSupportFrom: initializeSelectDate(
        state && state.higherBrainDysfunctionPersonsSupportFrom
      ),
      higherBrainDysfunctionPersonsSupportTo: initializeSelectDate(
        state && state.higherBrainDysfunctionPersonsSupportTo
      )
    }
  };
};

export default initialValues;
